import { useCallback, useEffect, useRef } from 'react';
import {
	// useNavigate,
	useSearchParams
} from 'react-router-dom';
import {
	makeStyles,
	TabList,
	Tab,
} from '@fluentui/react-components';
import AutomationRuleFormProvider, {AutomationRuleForm, useAutomationRuleFormProvider} from './form';
import AutomationRuleHeader from './header';
import AutomationRuleTriggerProvider from './form/trigger/use-trigger';
import AutomationRuleConditionsProvider from './form/conditions/use-conditions';
import AutomationRuleActionProvider from './form/actions/use-actions';
import AutomationRuleActivity from './activity';
import AutomationRuleSettings from './settings';

import { Page } from 'src/components/ui/page';
import { AutomationRuleSettingsProvider, useAutomationRuleSettings } from './settings/provider';
import { useTemplatesDialogHandlers } from '../home/rules/templates/handlers';

function PageContent() {
	const classes = useStyles();
	const { callTemplateAndMountForm } = useTemplatesDialogHandlers();
	const { checkProjectAuthorization } = useAutomationRuleSettings();

	const {
		resetValidations,
		loadAutomationRuleById,
		setLoadingPage,
		tab,
		setTab,
		loadingPage,
		setStatus,
		setActor
	} = useAutomationRuleFormProvider();

	const [searchParams] = useSearchParams();

	const onChangeTab = useCallback((tabValue: string) => () => {
		setTab(tabValue);
	}, []);

	const setInitialActor = useCallback((
		userAuthorization: boolean,
		servicePrincipalAuthorization: boolean,
	) => {
		if (servicePrincipalAuthorization) {
			setActor('service-principal');
		}

		if (userAuthorization) {
			setActor('user');
		}
	}, []);

	const changedToDisabledStatus = useCallback((
		userAuthorization: boolean,
		servicePrincipalAuthorization: boolean,
	) => {
		if (!userAuthorization && !servicePrincipalAuthorization) {
			setStatus('disabled');
		}
	}, []);

	const checkIfIsAnUpdateCase = useCallback(async () => {
		const id = searchParams.get('id');

		if (id) {
			await loadAutomationRuleById(id);
			await checkProjectAuthorization();

			setLoadingPage(false);
		}

		if (!id) {
			const result = await checkProjectAuthorization();
			result && changedToDisabledStatus(
				result.userAuthorization,
				result.servicePrincipalAuthorization
			);

			result && setInitialActor(
				result.userAuthorization,
				result.servicePrincipalAuthorization
			);

			const templateId = searchParams.get('template-id');

			if (templateId) {
				callTemplateAndMountForm(templateId);
			}

			setLoadingPage(false);
		}
	}, [searchParams]);

	const init = useRef(true);
	useEffect(() => {
		if (init.current) {
			resetValidations();

			init.current = false;
			checkIfIsAnUpdateCase();
		}
	}, []);

	return (
		<Page>		
			<div className={classes.root}>
				<div className={classes.topContent}>
					<AutomationRuleHeader />
					<TabList selectedValue={tab}>
						<Tab
							disabled={loadingPage}
							onClick={onChangeTab('rule')}
							value="rule"
						>
							Rule
						</Tab>
						<Tab
							disabled={loadingPage}
							onClick={onChangeTab('settings')}
							value="settings"
						>
							Settings
						</Tab>
						<Tab
							disabled={loadingPage}
							onClick={onChangeTab('activity')}
							value="activity"
						>
							Activity
						</Tab>
					</TabList>
				</div>
				<div className={classes.tabContent}>
					{tab === 'rule' && <AutomationRuleForm />}
					{tab === 'settings' && <AutomationRuleSettings />}
					{tab === 'activity' && <AutomationRuleActivity />}
				</div>
			</div>
		</Page>
	);
}

const AutomationRulePage = () => (
	<AutomationRuleTriggerProvider>
		<AutomationRuleConditionsProvider>
			<AutomationRuleActionProvider>
				<AutomationRuleSettingsProvider>
					<AutomationRuleFormProvider>
						<PageContent />
					</AutomationRuleFormProvider>
				</AutomationRuleSettingsProvider>
			</AutomationRuleActionProvider>
		</AutomationRuleConditionsProvider>
	</AutomationRuleTriggerProvider>
);

const useStyles = makeStyles({
	root: {
		height: 'auto',
		width: '100%',
		boxSizing: 'border-box',
	},
	topContent: {
		top: '0',
		left: '0',
		padding: '16px 16px',
		width: '100%',
		boxSizing: 'border-box',
	},
	tabContent: {
		padding: '0px 24px',
		height: '100%',
		boxSizing: 'border-box',
	},
	loadingPageRoot: {
		height: '100%',
		width: '100%',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
	},
	submitLoading: {
		position: 'absolute',
		width: '100%',
		height: '100%',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		zIndex: 1,
		backgroundColor: 'rgba(0,0,0,0.2)'
	}
});

export default AutomationRulePage;
